import { InferType } from "yup";
import { RubricRuleset } from "./types";
import {
  BasicCanadianCriminalRecordCriteriaSchemaV1,
  CodeSchema,
} from "../../pages/adjudication/types/basic-canadian-criminal-record-criteria-schema-v1";
import { InferClientPortalAPI, useClientPortalAPI } from "@certn/api";
import { useTranslation } from "react-i18next";
import { ActionList, ActionListItem, Stack, Text } from "@certn/cascadia";
import { DataDisplay, GhostIconButton, StatusTag } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import {
  AdjudicationStatus,
  AdjudicationStatusTag,
} from "../../utils/renderStatusTag";
import { getCheckTypeName } from "../../pages/adjudication/utils";

type Props = {
  ruleset: RubricRuleset & {
    criteria: InferType<typeof BasicCanadianCriminalRecordCriteriaSchemaV1>;
  };
  selectComponent?: React.ReactNode;
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  selectedCode: string | null;
  setSelectedCode: (code: string | null) => void;
  subcategoriesData: InferClientPortalAPI<"/api/internal/client-portal/adjudication-rubrics/subcategories/">;
};

export function PreviewBasicCanadianCriminalRecordCheckRuleset({
  ruleset,
  selectComponent,
  selectedCategory,
  setSelectedCategory,
  selectedCode,
  setSelectedCode,
  subcategoriesData,
}: Props) {
  const { t } = useTranslation();

  const { data: codesData } = useClientPortalAPI(
    selectedCategory
      ? "/api/internal/client-portal/adjudication-rubrics/subcategory-codes/"
      : null,
    {
      subcategory: selectedCategory,
    }
  );

  const selectedCategoryData = selectedCategory
    ? ruleset.criteria.ruleset.find((cat) => cat.category === selectedCategory)
    : null;

  const selectedCodeData =
    selectedCode && selectedCategoryData
      ? selectedCategoryData.codes.find((code) => code.code === selectedCode)
      : null;

  const shouldShowCategoryCodesList =
    selectedCategory && selectedCategoryData && !selectedCode;
  const shouldShowCodeDetail = selectedCode && selectedCodeData;

  const getHeading = () => {
    if (shouldShowCodeDetail) {
      const codeData = codesData?.find((item) => item.key === selectedCode);
      return codeData?.code_label || selectedCode;
    }
    if (shouldShowCategoryCodesList) {
      return (
        subcategoriesData?.find((item) => item.value === selectedCategory)
          ?.label || "--"
      );
    }
    return getCheckTypeName(ruleset.check_type, t);
  };

  return (
    <Stack gap="space-4">
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack gap="space-0">
          <Text style="global/heading-large">{getHeading()}</Text>
          {(shouldShowCategoryCodesList || shouldShowCodeDetail) && (
            <Text style="form/body-secondary">
              {getCheckTypeName(ruleset.check_type, t)}
            </Text>
          )}
        </Stack>
        {selectComponent}
      </Stack>

      {shouldShowCodeDetail && selectedCodeData ? (
        <CodeDetailView code={selectedCodeData} />
      ) : shouldShowCategoryCodesList ? (
        <CategoryCodesList
          codesData={codesData}
          selectedCategoryData={selectedCategoryData}
          setSelectedCode={setSelectedCode}
        />
      ) : (
        <CategoryList
          ruleset={ruleset}
          setSelectedCategory={setSelectedCategory}
          subcategoriesData={subcategoriesData}
        />
      )}
    </Stack>
  );
}

function CategoryList({
  ruleset,
  setSelectedCategory,
  subcategoriesData,
}: Pick<Props, "ruleset" | "setSelectedCategory" | "subcategoriesData">) {
  return (
    <Stack gap="space-4">
      <ActionList>
        {ruleset.criteria.ruleset.map((category) => {
          const categoryLabel =
            subcategoriesData?.find((item) => item.value === category.category)
              ?.label || category.category;
          return (
            <ActionListItem
              key={category.category}
              className={css({
                borderBottom: "1px solid var(--border-base)",
                paddingBottom: "var(--space-4)",
                paddingTop: "var(--space-4)",
              })}
              testID={`category-${category.category}`}
              title={categoryLabel}
              trailing={
                <GhostIconButton
                  icon="chevron_right"
                  onPress={() => setSelectedCategory(category.category)}
                />
              }
            />
          );
        })}
      </ActionList>
    </Stack>
  );
}

function CategoryCodesList({
  codesData,
  selectedCategoryData,
  setSelectedCode,
}: {
  codesData: InferClientPortalAPI<"/api/internal/client-portal/adjudication-rubrics/subcategory-codes/">;
  selectedCategoryData: InferType<
    typeof BasicCanadianCriminalRecordCriteriaSchemaV1
  >["ruleset"][number];
  setSelectedCode: (code: string | null) => void;
}) {
  const hasCustomConditions = (code: InferType<typeof CodeSchema>) => {
    const oneOffenseConditions = [
      code.one_offense.zero_to_three_years_since_offense_outcome,
      code.one_offense.three_to_ten_years_since_offense_outcome,
      code.one_offense.ten_or_more_years_since_offense_outcome,
    ];

    const twoOrMoreOffensesConditions = [
      code.two_or_more_offenses.zero_to_three_years_since_offense_outcome,
      code.two_or_more_offenses.three_to_ten_years_since_offense_outcome,
      code.two_or_more_offenses.ten_or_more_years_since_offense_outcome,
    ];

    return [...oneOffenseConditions, ...twoOrMoreOffensesConditions].some(
      (condition) => condition && condition !== "NOT_SET"
    );
  };

  const getCodeScore = (code: InferType<typeof CodeSchema>) => {
    const globalOutcome = code.global_outcome;
    return hasCustomConditions(code) ? "CUSTOM" : globalOutcome;
  };

  return (
    <Stack gap="space-4">
      <ActionList>
        {selectedCategoryData.codes.map((code, index) => {
          const codeData = codesData?.find((item) => item.key === code.code);
          const codeLabel = codeData?.code_label || code.code;
          const offencesLabel = codeData?.label || "--";
          const score = getCodeScore(code);
          const isClickable = hasCustomConditions(code);

          return (
            <ActionListItem
              key={`${code.code}-${index}`}
              className={css({
                borderBottom: "1px solid var(--border-base)",
                paddingBottom: "var(--space-4)",
                paddingTop: "var(--space-4)",
              })}
              testID={`code-${code.code}`}
              title={
                <Stack gap="space-1">
                  <Stack orientation="horizontal" align="center" gap="space-2">
                    <StatusTag
                      fill="background-accent-neutral-weak"
                      color="text-base"
                      statusInfoIcon={false}
                    >
                      {codeLabel}
                    </StatusTag>
                  </Stack>
                  <Text style="input/helper">{offencesLabel}</Text>
                </Stack>
              }
              trailing={
                <Stack orientation="horizontal" align="center" gap="space-2">
                  {AdjudicationStatusTag(score as AdjudicationStatus)}
                  {isClickable && (
                    <GhostIconButton
                      icon="chevron_right"
                      onPress={() => setSelectedCode(code.code)}
                    />
                  )}
                </Stack>
              }
            />
          );
        })}
      </ActionList>
    </Stack>
  );
}

function CodeDetailView({ code }: { code: InferType<typeof CodeSchema> }) {
  const { t } = useTranslation();

  return (
    <DataDisplay
      renderTopMargin={false}
      columns={[
        {
          key: "condition",
          name: t("Condition"),
          isRowHeader: true,
          render: ({ condition }) => (
            <Text style="input/helper">{condition}</Text>
          ),
        },
        {
          key: "rules",
          name: t("Rules"),
          render: ({ rules }) =>
            AdjudicationStatusTag(rules as AdjudicationStatus),
        },
      ]}
      rows={[
        {
          key: "one-offense-0-3",
          condition: t("1 offence - 0-3 years"),
          rules:
            code.one_offense.zero_to_three_years_since_offense_outcome ||
            "NOT_SET",
        },
        {
          key: "one-offense-3-10",
          condition: t("1 offence - 3-10 years"),
          rules:
            code.one_offense.three_to_ten_years_since_offense_outcome ||
            "NOT_SET",
        },
        {
          key: "one-offense-10-plus",
          condition: t("1 offence - 10+ years"),
          rules:
            code.one_offense.ten_or_more_years_since_offense_outcome ||
            "NOT_SET",
        },
        {
          key: "two-plus-offenses-0-3",
          condition: t("2+ offences - 0-3 years"),
          rules:
            code.two_or_more_offenses
              .zero_to_three_years_since_offense_outcome || "NOT_SET",
        },
        {
          key: "two-plus-offenses-3-10",
          condition: t("2+ offences - 3-10 years"),
          rules:
            code.two_or_more_offenses
              .three_to_ten_years_since_offense_outcome || "NOT_SET",
        },
        {
          key: "two-plus-offenses-10-plus",
          condition: t("2+ offences - 10+ years"),
          rules:
            code.two_or_more_offenses.ten_or_more_years_since_offense_outcome ||
            "NOT_SET",
        },
      ]}
    />
  );
}
